import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import * as budgetApi from '../../lib/api/budget';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { downloadFile } from '../../utils/fileCodeUtils';
import { getYearOptions, thisYear } from '../../utils/calcDatetime';
import { useBoolean } from '../../hooks/useBoolean';
import EmpModalContainer from '../common/form/modal/EmpModalContainer';
import FourMajorInsuranceModalContainer from './FourMajorInsuranceModalContainer';
import IndividualSalaryUploadModalContainer from './IndividualSalaryUploadModalContainer';
import MonthSalaryForm from '../../components/individual-salary-input/MonthSalaryForm';

//----------------------------------------------
const arrYears = getYearOptions(3, 3);
const defaultYear = thisYear;
//----------------------------------------------

const IndividualSalaryAndMajorInsureInputContainer = () => {
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentSalaryData, setCurrentSalaryData] = useState(null);
  const [miPercents, setMiPercents] = useState(null);

  const isLoading = useBoolean();
  const { enqueueSnackbar } = useSnackbar();

  const onLoadData = useCallback(async () => {
    isLoading.onTrue();

    try {
      const res = await budgetApi.getIndividualSalary({
        year: selectedYear,
        usrUuid: selectedUser?.usrUuid,
      });

      if (res.data?.info) {
        console.log(JSON.stringify(res.data.info));

        const orderedSalaryData = _.orderBy(res.data?.info?.salaries, 'month');

        const loadedData = {
          ...res.data.info,
          salaries: orderedSalaryData,
          usrNm: selectedUser?.usrNm,
        };
        setCurrentSalaryData(loadedData);
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
    isLoading.onFalse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.usrUuid, selectedYear]);

  const getMIInfo = useCallback(async () => {
    try {
      const res = await budgetApi.getAllMajorInsurance({
        year: selectedYear,
      });

      if (res.data?.info) {
        console.log(JSON.stringify(res.data.info));
        setMiPercents(res.data.info);
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // 년도와 사원이 선택되면 API 호출함. (변경되어도 재호출)
  useEffect(() => {
    if (selectedYear && selectedUser) {
      onLoadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedUser]);

  useEffect(() => {
    if (selectedYear) {
      getMIInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // show employee
  const showEmpDialog = useBoolean();
  const selectedEmployee = (emp) => {
    setSelectedUser(emp);
  };

  const showAllModal = useBoolean();
  // all modal
  const handleAllModal = useCallback(() => {
    showAllModal.onTrue();
  }, [showAllModal]);

  const refreshOnAllClose = () => {
    showAllModal.onFalse();
    // dispatch(listSalesCosts(makeFilters(defaultFilters)));
  };

  // upload
  const showUpload = useBoolean();

  const handleUpload = useCallback(() => {
    showUpload.onTrue();
  }, [showUpload]);

  const handleDownload = async () => {
    try {
      const blob = await budgetApi.downloadExlByIndividualSalary({
        year: selectedYear,
      });
      const filename = blob.headers['filename'];

      downloadFile(blob, decodeURIComponent(filename), 'xlsx');
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const refreshOnClose = () => {
    showUpload.onFalse();
    // dispatch(listSalesCosts(makeFilters(defaultFilters)));
  };

  const content =
    !selectedYear || !selectedUser ? (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Typography variant="subtitle2">
          적용년도와 사원을 선택하세요
        </Typography>
      </Box>
    ) : !currentSalaryData ? (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Typography variant="subtitle2">
          해당 사원의 데이터를 가져오지 못했습니다.
        </Typography>
      </Box>
    ) : (
      <Stack spacing={1}>
        <MonthSalaryForm
          currentUser={selectedUser}
          currentYear={selectedYear}
          currentSalaryData={currentSalaryData}
          onLoadData={onLoadData}
          miPercents={miPercents}
        />
      </Stack>
    );

  return (
    <Box>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            py: 1,
          }}
        >
          <Button variant="contained" color="info" onClick={handleAllModal}>
            전체 적용
          </Button>
          <Button variant="contained" color="success" onClick={handleUpload}>
            업로드
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleDownload}
          >
            다운로드
          </Button>
        </Stack>
        <Stack
          spacing={2}
          sx={{
            width: '100%',
            bgcolor: 'grey.50',
            borderColor: 'grey.300',
            p: 1,
            m: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              py: 1,
            }}
          >
            <Box sx={{ width: '20%', textAlign: 'center' }}>
              <Typography variant="subtitle2">적용년도</Typography>
            </Box>
            <Box>
              <FormControl variant="standard" size="small" fullWidth>
                <Select
                  variant="outlined"
                  size="small"
                  labelId="selected-year-label"
                  id="selected-year"
                  displayEmpty
                  sx={{ width: 120 }}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {arrYears &&
                    arrYears.map((item, index) => (
                      <MenuItem key={index} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              py: 1,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ width: '20%', textAlign: 'center' }}>
              <Typography variant="subtitle2">사용자</Typography>
            </Box>
            <Box>
              <TextField
                size="small"
                required={true}
                variant="outlined"
                fullWidth
                id="usrNm"
                name="usrNm"
                value={selectedUser?.usrNm || ''}
                disabled
              />
            </Box>
            <Box>
              <Button
                variant="outlined"
                color="info"
                onClick={showEmpDialog.onTrue}
              >
                선택
              </Button>
            </Box>
            <Box>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setSelectedUser(null)}
              >
                사용자 초기화
              </Button>
            </Box>
          </Stack>
        </Stack>

        <Stack spacing={2}>{content}</Stack>

        {showEmpDialog.value && (
          <EmpModalContainer
            title="사원찾기"
            multi={false}
            open={showEmpDialog.value}
            handleClose={showEmpDialog.onFalse}
            selectedEmployee={selectedEmployee}
          />
        )}

        {showAllModal.value && (
          <FourMajorInsuranceModalContainer
            title="4대 보험요율 전체 적용"
            open={showAllModal.value}
            onClose={() => {
              showAllModal.onFalse();
            }}
            refreshOnClose={refreshOnAllClose}
          />
        )}

        {showUpload.value && (
          <IndividualSalaryUploadModalContainer
            title="개인별 급여 입력 엑셀 업로드"
            open={showUpload.value}
            onClose={() => {
              showUpload.onFalse();
            }}
            refreshOnClose={refreshOnClose}
          />
        )}
      </Stack>
    </Box>
  );
};

export default IndividualSalaryAndMajorInsureInputContainer;
